import { CatalogPlan } from '@brand-lolly/app/billing/types/plan.types';
import {
	PlanInterval,
	TrialKind
} from '@brand-lolly/app/billing/types/billing.enums';

/**
 * Pure helpers for the monthly/yearly plan interval logic shared by the
 * settings plans page and the pricing page. Kept framework-free so both the
 * `ManageSubscriptionPlanComponent` and `PricingPageComponent` (and the shared
 * {@link PlanIntervalToggleComponent}) compute identical prices / savings.
 */

/** True when any plan in the set has a yearly variant (toggle is meaningful). */
export function hasYearlyVariant(plans: CatalogPlan[]): boolean {
	return plans.some(plan => plan.interval === PlanInterval.YEAR);
}

/** Whether a paid plan matches the currently selected interval. */
export function matchesInterval(plan: CatalogPlan, annually: boolean): boolean {
	const interval = annually ? PlanInterval.YEAR : PlanInterval.MONTH;
	return plan.interval === interval;
}

/** A real trial is available on the plan (anything other than `none`). */
export function planHasTrial(plan: CatalogPlan): boolean {
	return !!plan.trial_kind && plan.trial_kind !== TrialKind.NONE;
}

/** Per-month price string (yearly plans divided by 12). */
export function toMonthlyPrice(plan: CatalogPlan): string {
	const price = parseFloat(plan.price);
	if (isNaN(price)) {
		return plan.price;
	}
	const monthly = plan.interval === PlanInterval.YEAR ? price / 12 : price;
	return monthly.toFixed(2);
}

/** Find the matching monthly tier for a yearly plan (same tier, paid). */
function monthlyTwin(
	plan: CatalogPlan,
	plans: CatalogPlan[]
): CatalogPlan | undefined {
	// Match by ALIAS (the yearly alias minus its `_yearly` suffix), NOT tier_level:
	// within one product tier_level can repeat across families (Creator Data API's
	// report_* and quick_* share tier_levels 1..6), which would mis-pair the twin.
	const monthAlias = plan.alias.replace(/_yearly$/, '');
	return plans.find(
		candidate =>
			candidate.interval === PlanInterval.MONTH &&
			candidate.alias === monthAlias &&
			candidate.trial_kind !== TrialKind.APP
	);
}

/** For a yearly plan: the matching monthly tier's NUMERIC price (null if none).
 *  Used to compute the struck "full annual" (monthly × 12) on the API card. */
export function monthlyTwinPrice(
	plan: CatalogPlan,
	plans: CatalogPlan[]
): number | null {
	const twin = monthlyTwin(plan, plans);
	if (!twin) {
		return null;
	}
	const price = parseFloat(twin.price);
	return isNaN(price) ? null : price;
}

/**
 * For a yearly plan: the matching monthly tier's price formatted for the
 * strikethrough ("$99"). Null for monthly/app-trial plans or when no twin.
 */
export function struckMonthlyPrice(
	plan: CatalogPlan,
	plans: CatalogPlan[]
): string | null {
	if (plan.interval !== PlanInterval.YEAR) {
		return null;
	}
	const twin = monthlyTwin(plan, plans);
	if (!twin) {
		return null;
	}
	const monthlyPrice = parseFloat(twin.price);
	if (isNaN(monthlyPrice) || monthlyPrice <= 0) {
		return null;
	}
	const rounded =
		monthlyPrice % 1 === 0 ? String(monthlyPrice) : monthlyPrice.toFixed(2);
	return `$${rounded}`;
}

/** Max "save N%" across tiers that have both a monthly and yearly variant. */
export function computeAnnualSavings(plans: CatalogPlan[]): number | null {
	let best: number | null = null;
	for (const plan of plans) {
		if (plan.interval !== PlanInterval.YEAR) continue;
		const twin = monthlyTwin(plan, plans);
		if (!twin) continue;
		const yearly = parseFloat(plan.price);
		const monthly = parseFloat(twin.price);
		if (isNaN(yearly) || isNaN(monthly) || monthly <= 0) continue;
		const percent = Math.round((1 - yearly / 12 / monthly) * 100);
		if (percent > 0 && (best === null || percent > best)) {
			best = percent;
		}
	}
	return best;
}

/**
 * How many months a year is free, when the annual term is a whole number of
 * monthly payments — `null` when it is not.
 *
 * "2 months free" is what the application ladder actually offers and what the
 * signed-off pricing page says: `_APPLICATION_ANNUAL_MONTHS = 10` in the seed,
 * so a year costs ten months at every rung. It is DERIVED rather than written
 * on the page, because the other product this control serves does not work that
 * way — the Creator Data API's year is a flat 5% off, which is 11.4 months and
 * has no whole-month reading at all. That one falls back to the percentage.
 *
 * The exactness check is what keeps the claim honest: a term that comes to 10.3
 * months is not "2 months free", and rounding it to say so would be a discount
 * we do not give.
 */
export function computeAnnualFreeMonths(plans: CatalogPlan[]): number | null {
	let best: number | null = null;
	for (const plan of plans) {
		if (plan.interval !== PlanInterval.YEAR) continue;
		const twin = monthlyTwin(plan, plans);
		if (!twin) continue;
		const yearly = parseFloat(plan.price);
		const monthly = parseFloat(twin.price);
		if (isNaN(yearly) || isNaN(monthly) || monthly <= 0) continue;
		const monthsPaid = yearly / monthly;
		// Within a tenth of a month of a whole number, so a rounding cent in the
		// catalogue does not silently drop the claim.
		if (Math.abs(monthsPaid - Math.round(monthsPaid)) > 0.1) continue;
		const free = 12 - Math.round(monthsPaid);
		if (free >= 1 && (best === null || free < best)) {
			// The SMALLEST saving across the tiers, not the largest: the label
			// stands for every card under it, and a claim one tier cannot honour
			// is a claim the page should not make.
			best = free;
		}
	}
	return best;
}
